<template>
  <v-data-table
    :fixed-header="false"
    :height="undefined"
    v-if="providerId"
    v-bind="dataTableAttrs"
    :headers="headersShown"
    :items="list"
    :loading="isLoading"
    :options.sync="options"
    :server-items-length="total"
    :items-per-page.sync="itemsPerPage"
    v-model="selectedItems"
    @update:items-per-page="handleItemsPerPageUpdated"
  >
    <template v-slot:top>
      <rs-table-title-bar
        class="mt-6"
        v-if="$refs.headSearch"
        title="Periyodik Tahakkuk Listesi"
        icon=""
        @clear-filters="$refs.headSearch.reset"
        @reload="loadList"
        :hide-edit="!can('edit-other-income-type')"
        :edit-enabled="selectedItems.length === 1"
        :show-delete="can('edit-other-income-type')"
        :delete-enabled="selectedItems.length > 0"
        :search.sync="search.query"
        :add-route="can('edit-other-income-type') ? '#' : null"
        @click:add="handleAddClick"
        @click:edit="handleEditClick"
        @click:delete="handleBatchDeleteClick"
      />

      <AutoAssessmentForm
        @saved="loadList()"
        :provider="originalData"
        ref="autoAssessmentForm"
      />

      <rs-confirm
        ref="confirmDelete"
        :loading="isLoading"
        @confirmed="handleDeleteConfirmed"
        @cancelled="$refs.confirmDelete.hide()"
      />
    </template>

    <!-- eslint-disable-next-line -->
    <template v-slot:body.prepend="{ headers }">
      <rs-table-head-search
        :headers="headers"
        :search.sync="search"
        ref="headSearch"
      />
    </template>

    <!-- eslint-disable-next-line -->
    <template v-slot:item.edit="{ item }">
      <a
        href="#"
        class="btn btn-icon btn-sm btn-clean"
        @click.prevent="handleEditClick(item)"
      >
        <i class="menu-icon mdi mdi-pencil"></i>
      </a>
    </template>

    <!-- eslint-disable-next-line -->
    <template v-slot:item.is_active="{ value }">
      <rs-table-cell-boolean :value="value" />
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.amount="{ value }">
      <rs-table-cell-number price :value="value" />
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.ends_on="{ value }">
      <rs-table-cell-date :value="value" />
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from "vuex";

import { debounce } from "@/core/plugins/debounce";
import { hasDataTable, hasPermissions } from "@/view/mixins";
import AutoAssessmentForm from "./AutoAssessmentForm";

export default {
  name: "AutoAssessmentList",
  mixins: [hasDataTable, hasPermissions],
  components: {
    AutoAssessmentForm,
  },
  props: {
    providerId: {
      type: Number,
      required: true,
    },
  },
  watch: {
    options: {
      handler() {
        this.loadList();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.loadList();
      }),
      deep: true,
    },
    providerId: {
      handler() {
        this.loadList();
      },
    },
  },
  computed: {
    ...mapGetters(["autoAssessmentTimingTypeList"]),
  },
  data() {
    return {
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      headers: [
        {
          text: "",
          value: "edit",
          searchable: false,
          sortable: false,
          width: "30px",
          align: "center",
          hide: () => !this.can("edit-other-income-type"),
        },
        {
          text: "Aktif",
          value: "is_active",
          searchable: "isActive",
          align: "center",
        },
        {
          text: "Tedarikçi",
          value: "provider",
          searchable: "text",
        },
        {
          text: "Tutar",
          value: "amount",
          searchable: "number",
          align: "end",
        },
        {
          text: "Tahakkuk Zamanlama Tipi",
          value: "issue_auto_assessment_timing_type",
          searchable: "select",
          options: () => this.autoAssessmentTimingTypeList,
        },
        {
          text: "Geçerlilik Bitiş Tarihi",
          value: "ends_on",
          searchable: "date",
        },
      ],
    };
  },
  methods: {
    loadList() {
      if (this.isLoading || !this.providerId) {
        return;
      }

      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );
      params.expense_type_id = [this.providerId];
      this.resetTable();
      this.isLoading = true;

      this.$api
        .query("expense/periodical-expenses", { params })
        .then((response) => {
          if (response.data && response.data.data) {
            this.list = response.data.data;
          }

          if (response.data && response.data.meta) {
            this.total = response.data.meta.total;
            this.options.page = response.data.meta.current_page;
            this.itemsPerPage = response.data.meta.per_page;
            this.options.itemsPerPage = this.itemsPerPage;
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    handleAddClick() {
      this.$refs.autoAssessmentForm.show(null, {
        provider_id: this.providerId,
      });
    },
    handleEditClick(item) {
      if (item.id) {
        this.$refs.autoAssessmentForm.show(item.id);
      } else {
        this.$refs.autoAssessmentForm.show(this.selectedItems[0].id);
      }
    },
    handleBatchDeleteClick() {
      this.$refs.confirmDelete.show(
        `${this.selectedItems.length} tane Periyodik Tahakkuk'u silmek istediğinizden emin misiniz?`
      );
    },
    handleDeleteConfirmed() {
      if (this.isLoading) return false;

      this.isLoading = true;
      const promises = [];

      for (const index in this.selectedItems) {
        const promise = this.$api.delete(
          `expense/periodical-expenses/${this.selectedItems[index].id}`
        );
        promises.push(promise);
      }

      Promise.all(promises)
        .then(() => {
          this.$toast.success("Seçilen Periyodik Tahakkuklar silindi");
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.$refs.confirmDelete.hide();
          this.isLoading = false;
          this.loadList();
        });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.loadList();
    });
  },
};
</script>
